import debounce from "@common/utils/debounce";
import { request, apiBase } from "@common/request";
import gaSC from "@common/utils/ga";
import BubbleHoverEffect from "@common/utils/bubble-hover-efffect";
import { assetUrl } from "@common/utils/asset-url";

/**
 * Header search — the v2 full-screen popup.
 *
 * The markup is a straight port of the .in engine's
 * views/api/_search-content.ejs, which is what search-popup-v2.scss is written
 * against. Structure, attribute names and copy all follow it:
 *
 *   .g-search-open#go-search-modal[data-type="g-search-popup-v2"]
 *     > .container[data-type="g-search-popup-v2-container"]
 *         #gSearchClose > img
 *         [data-type="search-row-1"] > [data-type="search-input-box"]
 *             > span > svg,  > input#gSearchInputBox
 *         [data-type="predefined-items"]
 *             > [data-type="predefined-items-row"]
 *                 > p.title
 *                 > [data-type="search-items-row"]
 *                     > a[data-type="search-item-grid"][data-ismerchant]
 *                         > div[data-type="content-item"] > p.title, p.desc > span
 *                     > .fresh-offer-grid.go-cpn-show
 *                         > .header-ele > img, p
 *                         > .content-ele > p.title, p.desc
 *                         > p.view-p
 *         .search-result
 *             > div.hidden#go_search_*
 *                 > strong > span.go_searchterm, span.go_count
 *                 > a > span
 *                 > div[datatype="search-results-ele"] > a[data-ismerchant]
 *
 * `datatype` without the hyphen is .in's spelling, in both its view and its
 * stylesheet, so it stays.
 *
 * WHERE IT COMES FROM
 *
 * .in renders that view server-side and the header JS appends the HTML
 * (`GET search/SearchPreSuggestions?pt=&pid=`). International has no such
 * route, so the same DOM is built here and filled from the JSON gateway:
 *
 *   GET /search/recommendations     -> the predefined rows (once per page)
 *   GET /search/auto-suggest?q=…    -> the result groups
 *
 * Dropped from .in's view: the banks, festivals, cities, brands, store-deals
 * and category-deals result groups. Those are India-only taxonomies —
 * International's auto-suggest returns stores (as `categories`) and coupons,
 * nothing else.
 */
const MIN_QUERY = 2;
/** 300ms — what the live app's useSearchAutoComplete uses (.in waits 750). */
const DEBOUNCE_MS = 300;

/**
 * The open/close choreography, timed exactly as .in's search.js runs it. The
 * numbers are not arbitrary — they interleave the loader's 750ms clip-path
 * animation with the popup's scale transition.
 */
const LOADER_SELECTOR = "g-search-popup-loader";
const T = {
  loaderPaint: 10, // let the loader paint before animating it
  loaderOpen: 450, // 'opening' -> 'opened'
  focus: 300, // after the popup starts opening
  loaderToPopupClose: 100, // loader 'closing' -> popup 'closing'
  popupClose: 300, // popup 'closing' -> drop body.open-search
  loaderRemove: 700, // -> remove the loader element
};

let modal = null;
let recommendationsLoaded = false;
let abortSuggest = null;

/**
 * CMS-noindexed store slugs — the popup must not offer a store its own page
 * asks crawlers to ignore. The gateway's auto-suggest and recommendations
 * carry no noindex field, so the worker publishes the flagged slugs as
 * /noindex-stores.json next to robots.txt (SitemapService) and the popup
 * filters against it. Missing file or failed fetch = empty set: the popup
 * degrades to unfiltered rather than broken.
 */
let noindexSlugs = null;
let noindexPromise = null;
function loadNoindexStores() {
  if (noindexPromise) return noindexPromise;
  noindexPromise = fetch("/noindex-stores.json")
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      noindexSlugs = new Set(
        ((data && data.slugs) || []).map((slug) => String(slug).toLowerCase()),
      );
    })
    .catch(() => {
      noindexSlugs = new Set();
    });
  return noindexPromise;
}
function isHiddenStore(slug) {
  return !!(noindexSlugs && slug && noindexSlugs.has(String(slug).toLowerCase()));
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function searchInit() {
  // The no-results page's "You May Try Searching Like" list is clickable and
  // re-runs the search. It lives on the search PAGE, not in the popup, but
  // @core loads this module everywhere so the binding belongs here — same as
  // .in.
  document.addEventListener("click", (event) => {
    const suggestion = event.target.closest(".nosearch > ul > li");
    if (!suggestion || suggestion.getAttribute("data-type") === "para") return;
    const term = suggestion.textContent.trim();
    if (term) window.location.href = searchPageUrl(term);
  });

  const trigger = document.getElementById("gSearchInput");
  const box = document.getElementById("gSearch");
  if (!trigger || !box || !apiBase()) return;

  // Kicked off at page load so the set is normally resolved long before the
  // first keystroke; the first open awaits it too, alongside recommendations.
  loadNoindexStores();

  // The header input is only a trigger — typing happens in #gSearchInputBox
  // inside the popup, which is what .in does and what the stylesheet assumes.
  const open = (event) => {
    event.preventDefault();
    trigger.blur();
    openSearch(box);
    gaSC("Search Box Click");
  };
  trigger.addEventListener("focus", open);
  trigger.addEventListener("click", open);

  // The magnifier opens it too, which .in binds separately.
  const icon = box.querySelector("span > svg");
  if (icon) icon.addEventListener("click", open);

  // Shortcuts, as .in: Ctrl+S opens, Ctrl+E or Escape closes.
  window.addEventListener("keydown", (event) => {
    const isOpen = document.body.classList.contains("open-search");

    if (event.ctrlKey && event.key.toLowerCase() === "s") {
      event.preventDefault();
      if (!isOpen) openSearch(box);
      return;
    }
    if (!isOpen) return;
    if (
      event.key === "Escape" ||
      (event.ctrlKey && event.key.toLowerCase() === "e")
    ) {
      event.preventDefault();
      closeSearch();
    }
  });
}

/**
 * The results page, as a full URL.
 *
 * goGlobal.WEB_URL is WEB_DOMAIN from the environment — the site's own origin,
 * not a hardcoded production one. Locally it is http://localhost:3000, so Enter
 * stays on the dev server; in prod it is https://grabon.com. Falls back to the
 * current origin if the global is missing, so a stale cached bundle can never
 * navigate off-site.
 */
function searchPageUrl(term) {
  const origin = (goGlobal && goGlobal.WEB_URL) || window.location.origin;
  return `${String(origin).replace(/\/+$/, "")}/search/?q=${encodeURIComponent(term)}`;
}

/**
 * The bubble loader.
 *
 * A body-level element whose ::before runs `bubbleAnimV2ForSearch` — a
 * clip-path circle that grows from a point. BubbleHoverEffect writes that point
 * as --_xValue-self / --_yValue-self, as a percentage of the VIEWPORT (the
 * third argument switches it from element-relative to viewport-relative), so
 * the wipe appears to burst out of the search box the user just clicked.
 *
 * Inside it, [data-type="wrapper"] holds the four bars of the loading wave;
 * they fade in with --_self-cont-opacity once the state reaches 'opened'.
 */
function buildLoader(box) {
  const loader = document.createElement("div");
  loader.setAttribute("data-type", LOADER_SELECTOR);
  loader.innerHTML =
    '<div data-type="wrapper"><span></span><span></span><span></span><span></span></div>';

  const rect = box.getBoundingClientRect();
  BubbleHoverEffect(loader, { offsetX: rect.left, offsetY: rect.top }, true);

  return loader;
}

async function openSearch(box) {
  const first = !modal;
  if (first) {
    modal = buildModal();
    box.appendChild(modal);
    wireModal();
  }

  // Loader first: the bubble wipes across, then the popup scales in behind it.
  let loader = document.querySelector(`[data-type="${LOADER_SELECTOR}"]`);
  if (!loader) {
    loader = buildLoader(box);
    document.body.appendChild(loader);
    await wait(T.loaderPaint);
    loader.setAttribute("data-state", "opening");
    await wait(T.loaderOpen);
    loader.setAttribute("data-state", "opened");
  }

  // .in awaits its pre-suggestions fetch here, so the popup never opens empty.
  // The exclusion set is awaited with it so the first render is already
  // filtered — after that both are settled and cost nothing.
  const recommendations = loadRecommendations();
  if (first) await Promise.all([recommendations, loadNoindexStores()]);

  document.body.classList.add("open-search");
  modal.setAttribute("data-state", "opening");

  const input = modal.querySelector("#gSearchInputBox");
  setTimeout(() => input && input.focus(), T.focus);

  gaSC("Header Search", "Open");
}

async function closeSearch() {
  if (!modal) return;

  if (abortSuggest) {
    abortSuggest();
    abortSuggest = null;
  }

  const loader = document.querySelector(`[data-type="${LOADER_SELECTOR}"]`);
  if (loader) loader.setAttribute("data-state", "closing");

  await wait(T.loaderToPopupClose);
  modal.setAttribute("data-state", "closing");

  await wait(T.popupClose);
  document.body.classList.remove("open-search");
  modal.classList.remove("searching");
  const input = modal.querySelector("#gSearchInputBox");
  if (input) input.value = "";
  resetGroups();
  showPredefined(true);

  // Removed rather than hidden: the next open rebuilds it at the new position,
  // which is what makes the bubble start from the search box every time.
  await wait(T.loaderRemove);
  if (loader && loader.parentNode) loader.parentNode.removeChild(loader);
}

// ── shell ───────────────────────────────────────────────────────────────────

/** The result groups exist up front and hidden, exactly as .in's view emits. */
const RESULT_GROUPS = [
  {
    id: "go_search_stores",
    cls: "sr-store",
    label: "IN STORES",
    href: "/stores/",
    link: "View All Stores",
  },
  // .in's second group is CATEGORIES; International's auto-suggest returns
  // coupons instead, and there is no coupon listing page to link to.
  {
    id: "go_search_coupons",
    cls: "sr-banks",
    label: "IN COUPONS",
    href: null,
    link: null,
  },
];

function buildModal() {
  const el = document.createElement("div");
  el.className = "g-search-open";
  el.id = "go-search-modal";
  el.setAttribute("data-type", "g-search-popup-v2");

  const groups = RESULT_GROUPS.map(
    (group) => `
      <div class="${group.cls} hidden" id="${group.id}">
        <strong>"<span class="go_searchterm"></span>" ${group.label} <span class="go_count"></span></strong>
        ${group.href ? `<a href="${group.href}"><span>${group.link}</span></a>` : ""}
        <div datatype="search-results-ele"></div>
      </div>`,
  ).join("");

  el.innerHTML = `
    <div data-type="g-search-popup-v2-container" class="container">
      <div id="gSearchClose" role="button" tabindex="0" aria-label="Close search">
        <img src="/assets/icons/close-dark.svg" alt="Close" width="20" height="20">
      </div>

      <div data-type="search-row-1">
        <div data-type="search-input-box">
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34" fill="none">
              <path d="M32 32L24.75 24.75M28.6667 15.3333C28.6667 22.6971 22.6971 28.6667 15.3333 28.6667C7.96954 28.6667 2 22.6971 2 15.3333C2 7.96954 7.96954 2 15.3333 2C22.6971 2 28.6667 7.96954 28.6667 15.3333Z" stroke="#9FA3AC" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <input spellcheck="false" id="gSearchInputBox" type="text"
                 placeholder="Search for stores, coupons &amp; offers..." aria-label="Search" />
        </div>
      </div>

      <div data-type="predefined-items"></div>

      <div class="search-result">${groups}</div>
    </div>`;
  return el;
}

function wireModal() {
  const input = modal.querySelector("#gSearchInputBox");
  const close = modal.querySelector("#gSearchClose");

  if (close) {
    close.addEventListener("click", closeSearch);
    close.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") closeSearch();
    });
  }

  const run = debounce((term) => {
    if (abortSuggest) abortSuggest();
    const controller = new AbortController();
    abortSuggest = () => controller.abort();

    request
      .get(`${apiBase()}/search/auto-suggest?q=${encodeURIComponent(term)}`, {
        signal: controller.signal,
      })
      .then((data) => {
        if (!data) return;
        fillGroups(term, data);
        modal.classList.add("searching");
        showPredefined(false);
        gaSC("Header Search", term);
      });
  }, DEBOUNCE_MS);

  input.addEventListener("input", () => {
    const term = input.value.trim();
    if (term.length < MIN_QUERY) {
      modal.classList.remove("searching");
      resetGroups();
      showPredefined(true);
      return;
    }
    run(term);
  });

  input.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    const term = input.value.trim();
    if (!term) return;

    // .in's rule: exactly ONE visible store result means the user has already
    // found what they wanted, so go straight there. Anything else — several
    // matches, or none — goes to the results page, which is now a real route.
    //
    // Read off the DOM rather than the last response: it is the rendered,
    // visible state that the user is reacting to.
    const group = modal.querySelector("#go_search_stores");
    const hits =
      group && !group.classList.contains("hidden")
        ? group.querySelectorAll(
            '[datatype="search-results-ele"] a[data-ismerchant="True"]',
          )
        : [];

    if (hits.length === 1 && hits[0].getAttribute("href")) {
      window.location.href = hits[0].getAttribute("href");
      return;
    }
    window.location.href = searchPageUrl(term);
  });
}

function showPredefined(visible) {
  const predefined =
    modal && modal.querySelector('[data-type="predefined-items"]');
  if (predefined) predefined.classList.toggle("hidden", !visible);
}

// ── predefined rows (recommendations) ───────────────────────────────────────

/** Fetched once per page; the promise is returned so the first open can await it. */
let recommendationsPromise = null;
function loadRecommendations() {
  if (recommendationsLoaded) return recommendationsPromise || Promise.resolve();
  recommendationsLoaded = true;

  recommendationsPromise = request
    .get(`${apiBase()}/search/recommendations`)
    .then((data) => {
      if (!data) {
        recommendationsLoaded = false; // let the next open retry
        return;
      }
      const host = modal.querySelector('[data-type="predefined-items"]');
      if (host) host.innerHTML = renderRecommendations(data);
    });

  return recommendationsPromise;
}

function renderRecommendations(data) {
  const stores = section(data.recommendedStores, "Recommended Stores");
  const categories = section(
    data.recommendedCategories,
    "Recommended Categories",
  );
  const offers = section(data.offersYouMayLike, "Offers you may Like");

  return [
    tileRow(stores, true),
    tileRow(categories, false),
    offerRow(offers),
  ].join("");
}

/** p.title sits INSIDE the row, above search-items-row — .in's nesting. */
function tileRow(block, isMerchant) {
  if (block.items.length === 0) return "";
  const tiles = block.items
    .map((item, i) => tile(item, i, isMerchant))
    .join("");
  if (!tiles) return "";
  return `<div data-type="predefined-items-row">
    <p class="title">${esc(block.title)}</p>
    <div data-type="search-items-row">${tiles}</div>
  </div>`;
}

function tile(item, index, isMerchant) {
  const name = item.name || "";
  if (!item.slug || !name) return "";
  // Belt to fillGroups' braces: a noindexed store never becomes a tile,
  // whichever list it arrived on (results or recommendations).
  if (isHiddenStore(item.slug)) return "";
  const count = Number(
    item.totalCoupons || item.totalCouponsAndOffers || item.offerCount || 0,
  );
  return `<a data-type="search-item-grid"
             data-pos="${index + 1}"
             href="/${esc(item.slug)}-coupons/"
             title="${esc(name)} Coupons"
             data-ismerchant="${isMerchant ? "True" : "False"}"
             style="--_backgroundImage: url(${esc(logoOf(item))})">
            <div data-type="content-item">
              <p class="title">${esc(name)}</p>
              <p class="desc"><span>${isMerchant ? count : "View"}</span> Coupons &amp; offers</p>
            </div>
          </a>`;
}

function offerRow(block) {
  if (block.items.length === 0) return "";
  const cards = block.items
    .map((item, index) => {
      const store = item.storeName || "";
      const flash = item.templateString || "";
      const title = item.title || "";
      if (!title && !flash) return "";
      const couponId = String(item.couponId || "").toLowerCase();
      const uses = Number(item.usesToday || 0);
      const logo = logoOf(item);
      return `<div class="fresh-offer-grid go-cpn-show"
                   data-ctype="sc_c" data-goid="${esc(couponId)}" data-retype="c"
                   data-type="${couponId ? "cp" : "dl"}" data-cpnpstn="${index + 1}"
                   data-pos="${index + 1}" title="${esc(store)} Coupons">
                <div class="header-ele">
                  ${logo ? `<img src="${esc(logo)}" alt="${esc(store)}" title="${esc(store)}" loading="lazy" />` : ""}
                  <p>${esc(store)}</p>
                </div>
                <div class="content-ele">
                  <p class="title">${esc(flash || title)}</p>
                  <p class="desc">${esc(title)}</p>
                </div>
                ${uses > 0 ? `<p class="view-p">${uses} Uses Today</p>` : ""}
              </div>`;
    })
    .join("");
  if (!cards) return "";

  return `<div data-type="predefined-items-row">
    <p class="title">${esc(block.title)}</p>
    <div data-type="search-items-row">${cards}</div>
  </div>`;
}

// ── results ─────────────────────────────────────────────────────────────────

/**
 * .in's `returnHtml`: set the term and count, unhide the group, fill its
 * results container. A group with no matches stays hidden.
 */
function fillGroups(term, data) {
  // Filtered BEFORE fillGroup so the "(N)" count matches what renders —
  // tile() alone would drop the card but leave the count one high.
  const stores = (Array.isArray(data.categories) ? data.categories : []).filter(
    (item) => !isHiddenStore(item.slug),
  );
  const coupons = Array.isArray(data.coupons) ? data.coupons : [];

  fillGroup("go_search_stores", term, stores, (item, i) => tile(item, i, true));
  fillGroup("go_search_coupons", term, coupons, couponResult);
}

function fillGroup(id, term, items, renderItem) {
  const group = modal.querySelector(`#${id}`);
  if (!group) return;

  const html = items.map(renderItem).join("");
  if (!html) {
    group.classList.add("hidden");
    return;
  }

  group.classList.remove("hidden");
  const termNode = group.querySelector(".go_searchterm");
  const countNode = group.querySelector(".go_count");
  if (termNode) termNode.textContent = term;
  if (countNode) countNode.textContent = `(${items.length})`;
  group.querySelector('[datatype="search-results-ele"]').innerHTML = html;
}

function resetGroups() {
  if (!modal) return;
  modal.querySelectorAll(".search-result > div").forEach((group) => {
    group.classList.add("hidden");
    const results = group.querySelector('[datatype="search-results-ele"]');
    if (results) results.innerHTML = "";
  });
}

function couponResult(item, index) {
  const title = item.title || "";
  if (!title) return "";
  const couponId = String(item.couponId || "").toLowerCase();
  const flash = item.templateString || "";
  return `<a data-type="search-item-grid"
             data-pos="${index + 1}"
             href="javascript:void(0)"
             class="go-cpn-show"
             data-ctype="sc_c" data-goid="${esc(couponId)}" data-retype="c" data-type="cp"
             title="${esc(title)}"
             data-ismerchant="False"
             style="--_backgroundImage: url(${esc(logoOf(item))})">
            <div data-type="content-item">
              <p class="title">${esc(flash || title)}</p>
              <p class="desc">${esc(item.storeName || "")}</p>
            </div>
          </a>`;
}

// ── helpers ─────────────────────────────────────────────────────────────────

/** The API returns {title, items}; tolerate a bare array too. */
function section(raw, fallbackTitle) {
  if (Array.isArray(raw)) return { title: fallbackTitle, items: raw };
  if (raw && Array.isArray(raw.items)) {
    return { title: raw.title || fallbackTitle, items: raw.items };
  }
  return { title: fallbackTitle, items: [] };
}

/**
 * Logo field names differ per endpoint: recommendations sends logoDesktop,
 * auto-suggest sends popularStoresBgDesktop. `logo` is usually null on both.
 */
function logoOf(item) {
  return assetUrl(
    item.logoDesktop ||
      item.popularStoresBgDesktop ||
      item.logoMobile ||
      item.popularStoresBgMobile ||
      item.storeLogo ||
      item.logo ||
      "",
  );
}

function esc(value) {
  return String(value == null ? "" : value).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );
}
